import { ListControllerProps, useListController } from "ra-core";
import { FC, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import useLocalListController from "@/components/list/useLocalListController";
import { RecPatch } from "@/dataProvider/tools";
import { ClientStatus, ClientManageDto, ClientType } from "@/gen/api/user";


const clientsDriverRes = 'settings-clients-driver-encrypttool'
const clientsSecurefileRes = 'settings-clients-securefile'

export const ClientsManageResources = { clientsDriverRes, clientsSecurefileRes }

const clientListOptions : {sorters: Record<string, (a:ClientManageDto & RecPatch, b:ClientManageDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => (a.registerAt || '') < (b.registerAt || '') ? 1 : -1,
    register_at: (a, b) => (a.registerAt || '') < (b.registerAt || '') ? -1 : 1,
    reviewed_at: (a, b) => (a.reviewedAt || '') < (b.reviewedAt || '') ? -1 : 1,
    revoked_at: (a, b) => (a.revokedAt || '') < (b.revokedAt || '') ? -1 : 1,
    suspended_at: (a, b) => (a.suspendedAt || '') < (b.suspendedAt || '') ? -1 : 1,
  }
}

type CtxType = 'drivers'|'encrypttool'|'securefile'
interface ClientManageStates {
  activeListCtx: CtxType;
  currTab: string;
  term?: string,
}

export interface ClientManageContextProps {
  states: ClientManageStates,
  listCtrls: {
    drivers: ListControllerProps<ClientManageDto & RecPatch>,
    securefile: ListControllerProps<ClientManageDto & RecPatch>,
  },
  activeListCtrl: ListControllerProps<ClientManageDto & RecPatch>,
  setTerm: (term?: string) => void;
  setCurrTab: (currTab: string) => void;
  setActiveListCtx: (ctx: CtxType) => void;
}

export const ClientManageContext = createContext<ClientManageContextProps>({} as any)

export const ClientManageContextProvider : FC<{value:ClientManageContextProps}> = ({children, value}) => (
  <ClientManageContext.Provider value={value}>
    {children}
  </ClientManageContext.Provider>
)

export const useClientManageContext = () => useContext(ClientManageContext)

export const useClientManageContextController = (initActiveListCtx: CtxType) : ClientManageContextProps => {
  const [states, setStates] = useState<ClientManageStates>({
    activeListCtx: initActiveListCtx,
    currTab: ClientStatus[initActiveListCtx !== 'securefile' ? ClientStatus.PendingApproval : ClientStatus.Enabled],
  })

  const listCtrlsRef = useRef({} as any)
  listCtrlsRef.current.drivers = 
    useLocalListController({ 
      resource: states.activeListCtx !== 'securefile' ? clientsDriverRes : 'noop', 
      basePath: '', 
      filter: { status: states.currTab }
    }, clientListOptions)
  listCtrlsRef.current.securefile = 
    useListController({ 
      resource: states.activeListCtx === 'securefile' ? clientsSecurefileRes : 'noop', 
      basePath: '', 
      sort: defaultSort,
    })
  const listCtrls : ClientManageContextProps['listCtrls'] = listCtrlsRef.current

  useEffect(() => {
    if (states.activeListCtx === 'securefile') {
      const v = listCtrls.securefile.filterValues
      listCtrls.securefile.setFilters({...v, term: states.term}, {})
    } else {
      listCtrls.drivers.setFilters(
        {
          clientType: (d:ClientManageDto) => { 
            const b = d.clientType === ClientType.EncryptionTool
            return states.activeListCtx === 'encrypttool' ? b : !b
          },
          clientName: states.term 
            ? ((d:ClientManageDto) => {
              return !!d.clientName && d.clientName.toLowerCase().includes(states.term!.toLowerCase())
            }) as any
            : null
        },
        {})
    }
  }, [states.activeListCtx, states.term, listCtrls])
  

  const actions = useMemo(() => ({
    setTerm: (term?:string) => {
      setStates(v => ({...v, term}))
    },
    setCurrTab: (currTab:string) => {
      setStates(v => {
        return {...v, currTab}
      })
    },
    setActiveListCtx: (ctx:CtxType) => {
      let resetPage = false
      setStates(v => {
        if (v.activeListCtx === ctx) { return v }
        const v1 = {...v, 
          activeListCtx: ctx,
          currTab: ClientStatus[ctx !== 'securefile' ? ClientStatus.PendingApproval : ClientStatus.Enabled],
        }
        resetPage = true
        return v1
      })
      if (resetPage) {
        (ctx !== 'securefile' ? listCtrls.drivers : listCtrls.securefile).setPage(1)
      }
    },
  }), [listCtrls])

  return ({
    states,
    listCtrls,
    activeListCtrl: states.activeListCtx !== 'securefile' ? listCtrls.drivers : listCtrls.securefile,
    ...actions,
  })
}


const defaultSort = {
  field: 'register_at',
  order: 'DESC'
}

